import React, { useEffect, useState, useRef } from 'react';

import {
  formatTafToHTML,
  tkofToRwyData,
  getTkofSettingsByIcao,
  getMagneticVariationFromTkofData,
  isTrainingMode,
  useOverflowObserver,
} from '../helpers';
import {
  timeConvert_Floor,
  getTrainingWXFromAllData,
  getTrainingSnowtamFromAllData,
  parseTrainingWX,
  getNOTAM,
  getLidoNotamText,
  isFlightPlanAirport,
} from '../utilities/helperfunctions';
import { DISABLE_SWIPE } from '../utilities/enums';
import { useDataServiceContext } from '../hooks/useDataServiceContext';
import { Modal } from './Modal';
import { RunwayData } from './RunwayData';
import { Tabs } from './Tabs';

const menuOptions = ['WX', 'RWY/Wind', 'NOTAM'];

export const AirportModal = ({
  isOpen,
  setIsOpen,
  handleSelectAlternateAirport,
  airport,
  taf,
  metar,
  snowtam,
  flightData,
  blockFuelFuel,
  blockFuelHours,
  adList,
  isContingencyAirport = false,
}) => {
  const { getNOTAMByICAO } = useDataServiceContext();
  const [selectedMenuOption, setSelectedMenuOption] = useState(menuOptions[0]);
  const [notamText, setNotamText] = useState('');
  const [notamLoading, setNotamLoading] = useState(false);
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const [isSwipeDisabled, setIsSwipeDisabled] = useState(false);

  useOverflowObserver(containerRef, contentRef, setIsSwipeDisabled);
  const [data, setData] = useState({
    airportICAO: '',
    airportTaf: undefined,
    airportMetar: undefined,
    airportSnowtam: undefined,
    airportData: undefined,
    airportRoute: undefined,
    alternateData: undefined,
    airportRaim: undefined,
    airportSpaceWeather: undefined,
  });

  const {
    airportICAO,
    airportData,
    airportTaf,
    airportMetar,
    airportRoute,
    airportSnowtam,
    alternateData,
    airportRaim,
    airportSpaceWeather,
  } = data;

  useEffect(() => {
    const airportICAO = airport?.sICAO?.[0] || airport?.sADICAO?.[0];

    const airportTaf = (taf || []).find(
      (t) => t?.icaoId === airportICAO
    );
    const airportMetar = (metar || []).find(
      (m) => m?.station_id?.[0] === airportICAO
    );
    const airportSnowtam = (snowtam || []).find(
      (s) => s?.sICAO?.[0] === airportICAO
    );

    const notamObj = getNOTAM(
      flightData?.NOTAM_List?.[0]?.NOTAM ?? [],
      airportICAO
    );

    const airportRaim = flightData?.sRAIM?.[0];
    const airportSpaceWeather = flightData?.sSpaceWeather?.[0];

    const airportData = (flightData?.Airport_List?.[0]?.AirportData || []).find(
      (a) => a?.sICAO?.[0] === airportICAO
    );
    const airportRoute = (
      flightData?.AltRoute_List?.[0]?.AlternateRoute || []
    ).find((r) => r?.sICAO?.[0] === airportICAO);
    const alternateData = (flightData?.Alt_List?.[0]?.Alternate || []).find(
      (a) => a?.sADICAO?.[0] === airportICAO
    );

    setData({
      airportICAO,
      airportData,
      airportTaf,
      airportMetar,
      airportSnowtam,
      airportRoute,
      alternateData,
      airportRaim,
      airportSpaceWeather,
    });
  }, [taf, metar, snowtam, airport, flightData]);

  const onSelectAlternateAirport = () => {
    if (alternateData) {
      handleSelectAlternateAirport(alternateData);
      setIsOpen(false);
    }
  };

  // Get training weather data if in training mode
  const trainingMode = isTrainingMode();
  const trainingWeatherData = trainingMode && flightData && airportICAO
    ? (() => {
      const trainingWX = getTrainingWXFromAllData(flightData, airportICAO);
      return trainingWX?.sWX ? parseTrainingWX(trainingWX.sWX) : null;
    })()
    : null;

  const trainingSnowtam =
    trainingMode && flightData && airportICAO
      ? getTrainingSnowtamFromAllData(flightData, airportICAO)
      : null;

  // Use training data if available, otherwise use props
  const effectiveMetarText = trainingWeatherData?.metar
    ? trainingWeatherData.metar
    : airportMetar?.raw_text;

  const effectiveTafText = trainingWeatherData?.taf
    ? trainingWeatherData.taf
    : airportTaf?.rawTAF;

  const effectiveSnowtam = trainingSnowtam || airportSnowtam;

  const rawTafData = effectiveTafText;
  const htmlFormattedTafData = effectiveTafText
    ? formatTafToHTML(effectiveTafText, airportICAO)
    : '';

  const selectedIcao = data?.airportICAO;
  const tkofData = getTkofSettingsByIcao(selectedIcao, adList);
  const magneticVariation = getMagneticVariationFromTkofData(tkofData);

  const getNOTAMText = async (airportICAO) => {
    if (!airportICAO) return '';

    const lidoNotamText = getLidoNotamText(flightData, airportICAO);
    if (lidoNotamText) {
      return lidoNotamText;
    }

    if (isFlightPlanAirport(airportICAO, flightData)) {
      return 'NOTAM data not found';
    }

    try {
      setNotamLoading(true);
      const notamData = await getNOTAMByICAO(airportICAO);

      if (!notamData) {
        return "NOTAM data not available.";
      }

      return notamData?.sNOTAM || notamData?.errorMsg || 'NOTAM data not found';
    } catch (error) {
      console.error("Error fetching NOTAM:", error);
      return "NOTAM data not available.";
    } finally {
      setNotamLoading(false);
    }
  };

  useEffect(() => {
    if (selectedMenuOption === 'NOTAM' && airportICAO) {
      getNOTAMText(airportICAO).then((text) => {
        setNotamText(text);
      });
    }
  }, [selectedMenuOption, airportICAO, flightData]);

  const isApprovedAlternate = alternateData?.sADFunction?.[0] === 'ApprovedAlternate';

  // Check if suitability check warning should be shown from NOTAM data
  const notamObj = getNOTAM(
    flightData?.NOTAM_List?.[0]?.NOTAM ?? [],
    airportICAO
  );
  const showSuitabilityCheckWarning = notamObj?.bShowSuitabilityCheckWarning?.[0] === 'true';

  const renderMenuOption = (menuOption) => {
    switch (menuOption) {
      case 'WX': {
        return (
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}
          >
            {airport?.sAirportFunction?.[0] !== 'DepartureAirport' &&
              airport?.sAirportFunction?.[0] !== 'ArrivalAirport' && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                  }}
                >
                  {/* Fuel table */}
                  <table>
                    <tbody>
                      <tr className='table-heading'>
                        <th>FUEL</th>
                        <th></th>
                        <th></th>
                      </tr>
                      <tr>
                        <th></th>
                        <th>TIME</th>
                        <th>FUEL</th>
                      </tr>
                      <tr>
                        <th>ALTN</th>
                        <th>
                          {isApprovedAlternate
                            ? 'N/A'
                            : (() => {
                              const timeValue = alternateData?.dbAlternateFuelDur_H?.[0];
                              if (timeValue === null || timeValue === undefined || timeValue === '') {
                                return 'NA';
                              }
                              return timeConvert_Floor(Number(timeValue));
                            })()}
                        </th>
                        <th>
                          {isApprovedAlternate
                            ? 'N/A'
                            : (() => {
                              const fuelValue = alternateData?.nAlternateFuel_KG;
                              if (fuelValue === null || fuelValue === undefined || fuelValue === '') {
                                return 'N/A';
                              }
                              const fuel = Array.isArray(fuelValue) ? fuelValue[0] : fuelValue;
                              if (fuel === null || fuel === undefined || fuel === '' || fuel === 0 || fuel === '0') {
                                return 'N/A';
                              }
                              return fuel;
                            })()}
                        </th>
                      </tr >
                      <tr>
                        <th> BLOCK</th>
                        <th>
                          {isApprovedAlternate || isContingencyAirport
                            ? 'N/A'
                            : (blockFuelHours
                              ? timeConvert_Floor(blockFuelHours)
                              : 'N/A')}
                        </th>
                        <th>
                          {isApprovedAlternate || isContingencyAirport
                            ? 'N/A'
                            : (blockFuelFuel ?? 'N/A')}
                        </th>
                      </tr>
                    </tbody >
                  </table >

                  {
                    isContingencyAirport ? (
                      <p style={{ margin: 0, fontWeight: 600 }} > 3 % ERA</p >
                    ) : (
                      <button
                        className='select-altn-button'
                        onClick={onSelectAlternateAirport}
                      >
                        Select ALTN
                      </button>
                    )}
                </div >
              )}

            {/* Route table */}
            <table>
              <tbody>
                <tr className='table-heading'>
                  <th>ROUTE</th>
                </tr>
                <tr>
                  <th>
                    {airport?.sAirportFunction?.[0] === 'DepartureAirport'
                      ? flightData?.sRouteDescription ?? 'N/A'
                      : airportRoute?.sRouteDescription
                        ? airportRoute.sRouteDescription +
                        (airportData?.sPlannedRunway
                          ? '/' + airportData.sPlannedRunway
                          : '')
                        : 'N/A'}
                  </th>
                </tr>
              </tbody>
            </table>

            {/* Metar table */}
            <div>
              <table>
                <tbody>
                  <tr className='table-heading'>
                    <th>METAR</th>
                  </tr>
                  <tr>
                    <th>{effectiveMetarText ?? 'METAR data not found'}</th>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Taf table */}
            <table>
              <tbody>
                <tr className='table-heading'>
                  <th>TAF</th>
                </tr>
                <tr>
                  <th>
                    {htmlFormattedTafData !== ''
                      ? htmlFormattedTafData
                      : 'TAF data not found'}
                  </th>
                </tr>
              </tbody>
            </table>

                <table>
                  <tbody>
                    <tr className='table-heading'>
                      <th>SNOWTAM</th>
                    </tr>
                    <tr>
                  <th style={{ whiteSpace: 'pre-wrap' }}>
                    {effectiveSnowtam?.sSnowtam || 'N/A'}
                  </th>
                    </tr>
                  </tbody>
                </table>

            {
              !trainingMode && (
                <table>
                  <tbody>
                    <tr className='table-heading'>
                      <th>RAIM</th>
                    </tr>
                    <tr>
                      <th style={{ whiteSpace: 'pre-wrap' }}>{airportRaim || 'N/A'}</th>
                    </tr>
                  </tbody>
                </table>
              )
            }

            {
              !trainingMode && (
                <table>
                  <tbody>
                    <tr className='table-heading'>
                      <th>Space Weather</th>
                    </tr>
                    <tr>
                      <th style={{ whiteSpace: 'pre-wrap' }}>{airportSpaceWeather || 'N/A'}</th>
                    </tr>
                  </tbody>
                </table>
              )
            }
          </div >
        );
      }
      case 'RWY/Wind': {
        const rwyDataArray = tkofData ? tkofToRwyData(tkofData) : [];
        const filtered = rwyDataArray.filter(
          (runway) => !/XR|VIS/i.test(runway.rwy)
        );
        const metarForRunwayData = effectiveMetarText
          ? { raw_text: effectiveMetarText }
          : airportMetar;

        return (
          <RunwayData
            metar={metarForRunwayData}
            rwyDataArray={filtered}
            selectedIcao={selectedIcao}
            magneticVariation={magneticVariation}
            tafData={rawTafData}
          />
        );
      }
      case 'NOTAM': {
        return (
          <div>
            <p style={{ fontSize: '18px', fontWeight: 700, marginBottom: '8px' }}>
              NOTAM {airportICAO}
            </p>
            <div
              ref={containerRef}
              className={isSwipeDisabled ? DISABLE_SWIPE : ''}
              style={{ overflowX: 'auto' }}
            >
              <div ref={contentRef} style={{ width: 'fit-content' }}>
                <pre style={{ marginBottom: '16px', marginTop: 0, fontSize: '12px' }}>
                  {notamLoading
                    ? 'Loading NOTAM data'
                    : notamText || 'NOTAM data not found'}
                </pre>
              </div>
            </div>
          </div>
        );
      }
      default: {
        const rwyDataArray = tkofData ? tkofToRwyData(tkofData) : [];
        const filtered = rwyDataArray.filter(
          (runway) => !/XR|VIS/i.test(runway.rwy)
        );
        const metarForRunwayData = effectiveMetarText
          ? { raw_text: effectiveMetarText }
          : airportMetar;

        return (
          <RunwayData
            metar={metarForRunwayData}
            rwyDataArray={filtered}
            selectedIcao={selectedIcao}
            magneticVariation={magneticVariation}
            tafData={rawTafData}
            allData={flightData}
          />
        );
      }
    }
  };

  return (
    <Modal isOpen={isOpen} handleClose={() => setIsOpen(false)}>
      {/* Modal header */}
      <div className='airport-modal'>
        <div className='airport-modal-header'>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <p className='airport-modal-title'>
                {airportICAO}
                {'/'}
                {airport?.sIATA?.[0] || airport?.sADIATA?.[0]}
              </p>
              <p className='airport-modal-title'>
                {airport?.sAirportName ?? airport?.sADName}
              </p>
            </div>
            <a
              href={`docunet://NAX-ABC-${airport?.sICAO?.[0] ?? airport?.sADICAO?.[0]
                }`}
              className='abc-link'
            >
              <p>ABC</p>
            </a>
          </div>
          <Tabs
            tabs={menuOptions}
            selectedTab={selectedMenuOption}
            handleChangeTab={(tab) => setSelectedMenuOption(tab)}
          />
        </div>
        {showSuitabilityCheckWarning && (
          <div
            style={{
              color: 'red',
              fontWeight: 600,
              marginBottom: '16px',
            }}
          >
            WX/NOTAM SUITABILITY CHECK BY FLIGHT PLANNING SYSTEM NOT PERFORMED FOR THIS AERODROME
          </div>
        )}
        {renderMenuOption(selectedMenuOption)}
      </div>
    </Modal>
  );
};
